/**
 * Stage IG-P0 + Phase 1 redesign:Instagram モジュール共通コンポーネント。
 *
 * Phase 1 で追加した 5 つの内部組件(MetricCard / SectionCard /
 * InlineWarningBanner / SegmentedControl / PostMediaBadge)は
 * spec instagram_frontend_redesign_spec.md §12 の指示によるもの。
 * 既存 9 つの組件名は API 不変で温存し、Phase 2-4 で page 側が
 * 旧名 / 新名どちらでも呼べるようにしている。
 *
 * globals.css は変更せず、本モジュール instagram.css に閉じたトークン定義。
 */

import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { AlertCircle, Inbox, RefreshCw } from "lucide-react";

import type {
  InstagramCapabilities,
  InstagramCapabilityKey,
  MediaType,
  WorkflowStatus,
} from "../types";

// ---------------------------------------------------------------------------
// ページヘッダ(旧来仕様 — Phase 2 で Module Hero へ移行予定)
// ---------------------------------------------------------------------------

export function InstagramPageHeader({
  title,
  breadcrumb,
  actions,
}: {
  title: string;
  breadcrumb: string;
  actions?: ReactNode;
}) {
  return (
    <div className="ig-page-header">
      <div className="ig-page-header-main">
        <h1 className="ig-page-title">{title}</h1>
        <div className="ig-breadcrumb">{breadcrumb}</div>
      </div>
      {actions ? <div className="ig-page-header-actions">{actions}</div> : null}
    </div>
  );
}

/** 接続アカウント表示(未接続でも必ず何か出す)。 */
export function AccountChip({
  accountName,
  connected,
}: {
  accountName: string | null;
  connected: boolean;
}) {
  return (
    <span className="ig-account-chip">
      <span className={`ig-account-chip-dot${connected ? "" : " is-off"}`} />
      接続アカウント:
      <span className="ig-account-chip-name">
        {accountName ? `@${accountName}` : "未接続"}
      </span>
    </span>
  );
}

export function RefreshButton({
  onClick,
  loading,
}: {
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      className="ig-button ig-button-secondary"
      onClick={onClick}
      disabled={loading}
    >
      <RefreshCw size={14} className={loading ? "ig-spin" : undefined} />
      更新
    </button>
  );
}

// ---------------------------------------------------------------------------
// サブナビ(設計書 §19.2:左メニューを膨らませずモジュール内で 2 階層目を持つ)
// Phase 1 追記:.ig-subnav--hero を当てると Module Hero の渐变上で白文字表示。
// 既存 .ig-subnav は素の背景で维持し、互換性を壊さない。
// ---------------------------------------------------------------------------

const SUBNAV_ITEMS: { to: string; label: string; end?: boolean }[] = [
  { to: "/instagram", label: "ダッシュボード", end: true },
  { to: "/instagram/inbox", label: "DM管理" },
  { to: "/instagram/comments", label: "コメント" },
  { to: "/instagram/content", label: "投稿" },
  { to: "/instagram/settings", label: "接続設定" },
];

export function InstagramSubnav({
  badges,
  variant,
}: {
  badges?: Partial<Record<string, number>>;
  /** "plain": 既存 .ig-subnav(素の背景) / "hero": Module Hero 渐变上 */
  variant?: "plain" | "hero";
}) {
  const navClass = variant === "hero" ? "ig-subnav ig-subnav--hero" : "ig-subnav";
  return (
    <nav className={navClass}>
      {SUBNAV_ITEMS.map((item) => {
        const badge = badges?.[item.to];
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `ig-subnav__item${isActive ? " is-active" : ""}`
            }
          >
            {item.label}
            {badge && badge > 0 ? (
              <span className="ig-subnav-badge">{badge > 99 ? "99+" : badge}</span>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// 状態バッジ
// ---------------------------------------------------------------------------

const STATUS_META: Record<WorkflowStatus, { label: string; className: string }> = {
  pending: { label: "未対応", className: "ig-badge-pending" },
  in_progress: { label: "対応中", className: "ig-badge-progress" },
  done: { label: "完了", className: "ig-badge-done" },
  spam: { label: "スパム", className: "ig-badge-spam" },
};

export function StatusBadge({ status }: { status: WorkflowStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  return <span className={`ig-badge ${meta.className}`}>{meta.label}</span>;
}

export const STATUS_LABEL: Record<WorkflowStatus, string> = {
  pending: "未対応",
  in_progress: "対応中",
  done: "完了",
  spam: "スパム",
};

// ---------------------------------------------------------------------------
// Loading / Empty / Error(設計書 §11.1:Demo 段階から正式な 4 状態を持つ)
// ---------------------------------------------------------------------------

export function LoadingState({ label = "読み込んでいます..." }: { label?: string }) {
  return (
    <div className="ig-state">
      <div className="ig-state-icon">
        <RefreshCw size={20} className="ig-spin" />
      </div>
      <div className="ig-state-desc">{label}</div>
    </div>
  );
}

export function EmptyState({
  title = "データがありません",
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="ig-state">
      <div className="ig-state-icon">
        <Inbox size={20} />
      </div>
      <div className="ig-state-title">{title}</div>
      {description ? <div className="ig-state-desc">{description}</div> : null}
      {action}
    </div>
  );
}

/**
 * エラー表示。
 * 設計書 §13:「不允许把所有错误都显示为『连接失败』」。
 * よって code をそのまま出し、再試行可能なときだけ再試行ボタンを出す。
 */
export function ErrorState({
  message,
  code,
  onRetry,
}: {
  message: string;
  code?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div className="ig-state">
      <div className="ig-state-icon is-error">
        <AlertCircle size={20} />
      </div>
      <div className="ig-state-title">読み込みに失敗しました</div>
      <div className="ig-state-desc">{message}</div>
      {code ? <div className="ig-state-detail">{code}</div> : null}
      {onRetry ? (
        <button type="button" className="ig-button ig-button-secondary" onClick={onRetry}>
          <RefreshCw size={14} />
          再試行
        </button>
      ) : null}
    </div>
  );
}

/** リスト用のスケルトン行。 */
export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="ig-skeleton" style={{ width: 38, height: 38, borderRadius: 999 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="ig-skeleton ig-skeleton-line" style={{ width: "45%" }} />
            <div className="ig-skeleton ig-skeleton-line" style={{ width: "80%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CapabilityGuard(設計書 §13)
// ---------------------------------------------------------------------------

/**
 * capability に応じて子要素の表示 / 無効化を切り替える。
 *
 * 設計書 §13 の 3 分岐:
 *   - 非対応       → 非表示 or 無効 + 理由表示
 *   - 対応だが未認可 → 「追加の許可が必要」
 *   - 一時的な失敗  → 再試行(ここではなく ErrorState の責務)
 *
 * 重要:UI の想定で使えるかを判断せず、必ず backend の capability を見る。
 */
export function CapabilityGuard({
  capabilities,
  require: requiredKey,
  mode = "hide",
  fallbackLabel,
  children,
}: {
  capabilities: InstagramCapabilities | null;
  require: InstagramCapabilityKey;
  /** hide: 非表示 / disable: 無効化して理由を tooltip 表示 */
  mode?: "hide" | "disable";
  fallbackLabel?: string;
  children: ReactNode;
}) {
  // capability 未取得の間は「まだ分からない」ので出さない(誤って押させない)
  if (!capabilities) return null;

  if (capabilities[requiredKey]) return <>{children}</>;

  if (mode === "hide") return null;

  return (
    <span
      className="ig-capability-disabled"
      title={fallbackLabel ?? "現在の接続方式ではこの機能を利用できません。"}
      style={{ display: "inline-flex", opacity: 0.5, cursor: "not-allowed" }}
      aria-disabled
    >
      {children}
    </span>
  );
}

// ===========================================================================
// Phase 1 で追加(spec §12)
//
// 既存 component 名 / API は不变のまま、追加で 5 つを export。
// Phase 2-4 で page 側が使用する想定。今は未使用でもビルドが通る状態。
// ===========================================================================

// ---------------------------------------------------------------------------
// MetricCard(spec §6.2):Dashboard / Analytics の KPI 用。
// ---------------------------------------------------------------------------

export type MetricCardProps = {
  label: string;
  value: string | number;
  /** 補助アイコン(背景色付き 36x36 丸) */
  icon?: ReactNode;
  /** 差分表示 "+12.4%" 等。null/undefined で非表示 */
  delta?: string | null;
  /** delta が負数扱い(赤色)か */
  negative?: boolean;
};

export function MetricCard({ label, value, icon, delta, negative }: MetricCardProps) {
  return (
    <div className="ig-metric-card">
      <div className="ig-metric-card__top">
        <span className="ig-metric-card__label">{label}</span>
        {icon ? <span className="ig-metric-card__icon">{icon}</span> : null}
      </div>
      <div className="ig-metric-card__value">{value}</div>
      {delta ? (
        <div className={`ig-metric-card__delta${negative ? " is-negative" : ""}`}>
          {delta}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionCard(spec §3.4):汎用 Card。.ig-card を薄くラップして header / body を統一。
// 既存 .ig-card スタイルとは独立。Phase 2 以降のページで採用。
// ---------------------------------------------------------------------------

export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  flush,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** true なら body padding 0 */
  flush?: boolean;
}) {
  const hasHeader = !!(title || subtitle || actions);
  return (
    <div className="ig-card">
      {hasHeader ? (
        <div className="ig-card__header">
          <div>
            {title ? <div className="ig-section-title">{title}</div> : null}
            {subtitle ? <div className="ig-caption">{subtitle}</div> : null}
          </div>
          {actions ? <div>{actions}</div> : null}
        </div>
      ) : null}
      <div className={flush ? "" : "ig-card__body"}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineWarningBanner(spec §7.5 / §14.3)
// Capability エラーや一時的な接続不可を「画面中央の巨大エラー」ではなく
// ページ上部の細い Banner で伝える。actionLabel / onAction があれば右端にボタン。
// ---------------------------------------------------------------------------

export function InlineWarningBanner({
  title,
  description,
  actionLabel,
  onAction,
  debugCode,
}: {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** 詳細エラーコード(developer 用、折り畳み details 内に表示) */
  debugCode?: string | null;
}) {
  return (
    <div className="ig-warning-banner" role="alert">
      <div className="ig-warning-banner__body">
        <div className="ig-warning-banner__title">{title}</div>
        {description ? (
          <div className="ig-warning-banner__desc">{description}</div>
        ) : null}
        {debugCode ? (
          <details style={{ marginTop: 6, fontSize: 11 }}>
            <summary style={{ cursor: "pointer", color: "#b45309" }}>
              詳細
            </summary>
            <code style={{ fontSize: 11, color: "#92400e" }}>{debugCode}</code>
          </details>
        ) : null}
      </div>
      {actionLabel && onAction ? (
        <button
          type="button"
          className="ig-button"
          onClick={onAction}
          style={{ flexShrink: 0 }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SegmentedControl(spec §7.2):状態絞り込み(タブ横並び compact バージョン)。
// items: { key, label }[] + value + onChange。
// ---------------------------------------------------------------------------

export type SegmentedItem<T extends string> = { key: T; label: string };

export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
}: {
  items: SegmentedItem<T>[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="ig-segmented" role="tablist">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="tab"
          aria-selected={value === it.key}
          className={`ig-segmented__item${value === it.key ? " is-active" : ""}`}
          onClick={() => onChange(it.key)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostMediaBadge(spec §9.2):投稿カード右上の種別バッジ。
// IMAGE / VIDEO / REEL / CAROUSEL_ALBUM / STORY をラベル変換。
// ---------------------------------------------------------------------------

const MEDIA_TYPE_LABEL: Record<MediaType, string> = {
  IMAGE: "画像",
  VIDEO: "動画",
  REEL: "リール",
  CAROUSEL_ALBUM: "カルーセル",
  STORY: "ストーリー",
};

export function PostMediaBadge({
  mediaType,
  className,
}: {
  mediaType: MediaType;
  className?: string;
}) {
  return (
    <span
      className={`ig-badge ig-badge--draft${className ? " " + className : ""}`}
    >
      {MEDIA_TYPE_LABEL[mediaType] ?? mediaType}
    </span>
  );
}
